import {
  Heart,
  Car,
  Calendar,
  Settings,
  Gauge,
  Fuel,
  Palette,
  Users,
  Hash,
  Info,
  Droplet,
  TrendingDown,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/context/ThemeContext";
import {
  GuestLicensePlateTease,
  LicensePlate,
} from "@/components/my-auctions/LicensePlate";
import { useAuth } from "@/hooks/useAuth";
import { SaleTypeBadge } from "@/components/ui/SaleTypeBadge";
import { Button } from "../ui/button";
import { DescriptionPopup } from "./DescriptionDialog";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ListingOverview } from "@/types/listing";
import {
  fuelTypeLabels,
  transmissionTypeLabels,
  getColorLabel,
  getBodyTypeLabel,
} from "@/i18n/labels/vehicleLabels";
import { vehicleCardUILabels } from "@/i18n/labels/vehicleCardLabels";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { useLanguage } from "@/context/LanguageContext";
import { VehicleFuelType, TransmissionType } from "@/types/enums";
import { formatMileage, getListingAuctionEndTime } from "@/utils/listingUtils";
import { CompactAuctionTimer } from "@/components/car-listing/bidding/TimeRemaining";
import { hapticFavoriteToggle } from "@/lib/native/hapticFeedback";
import { saveListingsScrollY } from "@/lib/native/listingsScrollRestore";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import {
  listingCardTransitionName,
  navigateToListingFromClick,
} from "@/lib/native/listingNavigation";
import { buildAuctionListingPath } from "@/lib/listingUrl";
import { ListingCardThumbnail } from "@/components/auctions/ListingCardThumbnail";

// TODO: will use BaseListing in future when everything is merged
interface CarCardProps {
  listing: ListingOverview;
  viewMode: "wide" | "narrow" | "grid";
  onFavoriteToggle?: (listingId: string) => void;
  /** Hide reg number badge (e.g. on /auctions listing grid). */
  hideLicensePlate?: boolean;
}

const CarCard = ({
  listing,
  viewMode,
  onFavoriteToggle,
  hideLicensePlate = false,
}: CarCardProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isGuest = !user;
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const { language } = useLanguage();
  const vcLabels = vehicleCardUILabels(language);
  const carCardLabels = auctionsLabels(language).carCard;
  const guestPlateTitle =
    carCardLabels.guestPlateLoginHint ??
    "Logga in för att se registreringsnummer";

  const goToLogin = (e: React.MouseEvent) => {
    e.stopPropagation();
    const redirect = `${location.pathname}${location.search}`;
    navigate(`/auth?redirect=${encodeURIComponent(redirect)}`);
  };

  const renderGridPlate = () => {
    if (!hideLicensePlate && listing.vehicleDetails.regNumber) {
      return (
        <div className="min-w-0">
          <LicensePlate
            lpn={listing.vehicleDetails.regNumber}
            className="!text-[9px] sm:!text-[9px]"
          />
        </div>
      );
    }
    if (isGuest) {
      return (
        <div className="min-w-0">
          <GuestLicensePlateTease
            className="!text-[9px]"
            title={guestPlateTitle}
            onLoginClick={goToLogin}
          />
        </div>
      );
    }
    return <span />;
  };

  const effectiveViewMode = viewMode;

  if (!listing?.vehicleDetails) {
    return null;
  }

  // Use isFavorite from listing object
  const isFavorite = listing.isFavorite;
  const auctionEndTime = getListingAuctionEndTime(listing);

  const isDirectPurchaseReduced =
    listing.listingType === "direct-purchase" &&
    listing.directPurchaseDetails?.originalPrice != null &&
    listing.directPurchaseDetails?.currentPrice != null &&
    listing.directPurchaseDetails.originalPrice >
      listing.directPurchaseDetails.currentPrice;

  const directPriceClass = isDirectPurchaseReduced
    ? "font-bold text-red-700 dark:text-red-400"
    : "font-bold text-emerald-700 dark:text-emerald-400";

  const auctionPriceClass = "font-bold text-amber-600 dark:text-amber-400";

  const cardShadowClass =
    "shadow-md hover:shadow-lg transition-shadow duration-200";

  const { isNative } = useNativePlatform();

  const handleCardClick = (e: React.MouseEvent) => {
    if (!listing.campaignId && !isNative) return;
    try {
      saveListingsScrollY(
        location.pathname,
        location.search,
        window.scrollY,
      );
    } catch {
      // ignore
    }
    if (isNative) {
      void navigateToListingFromClick(navigate, listing, e, {
        from: `${location.pathname}${location.search}`,
        fromScrollY: window.scrollY,
      });
      return;
    }
    if (listing.campaignId) {
      navigate(buildAuctionListingPath(listing.campaignId, listing), {
        state: {
          from: `${location.pathname}${location.search}`,
          fromScrollY: window.scrollY,
        },
      });
    }
  };

  const cardTransitionStyle = isNative
    ? ({ viewTransitionName: listingCardTransitionName(listing.id) } as React.CSSProperties)
    : undefined;

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    hapticFavoriteToggle();
    if (onFavoriteToggle) {
      onFavoriteToggle(listing.id);
    }
  };

  // TODO: move to utils
  const getColorHex = (colorName: string | undefined | null): string => {
    if (
      colorName == null ||
      typeof colorName !== "string" ||
      !colorName.trim()
    ) {
      return "#808080";
    }
    const colorMap: Record<string, string> = {
      vit: "#ffffff",
      svart: "#000000",
      blå: "#0066cc",
      silver: "#c0c0c0",
      röd: "#cc0000",
      grå: "#808080",
      grön: "#00cc00",
      gul: "#ffff00",
      orange: "#ff6600",
      brun: "#8b4513",
      beige: "#f5f5dc",
      lila: "#800080",
      rosa: "#ffc0cb",
    };
    return colorMap[colorName.toLowerCase()] || "#808080";
  };

  const handleDescriptionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDescriptionPopup(true);
  };

  if (effectiveViewMode === "grid") {
    return (
      <>
        <Card
          className={`overflow-hidden ${cardShadowClass} cursor-pointer group animate-fade-in p-0 h-full`}
          style={cardTransitionStyle}
          onClick={handleCardClick}
          onMouseEnter={() => setCardHovered(true)}
          onMouseLeave={() => setCardHovered(false)}
        >
          <div className="flex h-full min-w-0 flex-col">
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
              <ListingCardThumbnail
                src={listing.thumbnail}
                hoverSrc={listing.hoverThumbnail}
                alt={listing.title}
                className="absolute inset-0 w-full h-full"
                imgClassName="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                preloadHover={cardHovered}
              />
              <div className="absolute left-1.5 top-1.5 z-10 flex items-center gap-1">
                {listing.listingType && (
                  <SaleTypeBadge
                    saleType={
                      listing.listingType === "direct-purchase"
                        ? "direct-purchase"
                        : "auction"
                    }
                    className="text-[10px]"
                  />
                )}
              </div>
              <button
                className="absolute right-1.5 top-1.5 z-10 rounded-full bg-black/50 p-1.5 backdrop-blur-sm transition hover:bg-black/70"
                onClick={handleFavoriteToggle}
                aria-label={
                  isFavorite
                    ? carCardLabels.removeFavorite
                    : carCardLabels.addFavorite
                }
              >
                <Heart
                  className={`h-4 w-4 ${
                    isFavorite ? "fill-red-500 text-red-500" : "text-white"
                  }`}
                />
              </button>
            </div>
            <div
              className={`flex flex-1 flex-col gap-1 p-2.5 ${
                isDark ? "bg-black/40" : "bg-white/95"
              }`}
            >
              <h3
                className={`text-[13px] font-semibold leading-snug ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {listing.title}
              </h3>
              <div
                className={`flex flex-col gap-0.5 text-[9px] md:text-[11px] font-medium leading-tight ${
                  isDark ? "text-white/85" : "text-gray-700"
                }`}
              >
                <div className="grid grid-cols-3 gap-x-2 items-center min-w-0">
                  {listing.vehicleDetails.year != null ? (
                    <span className="inline-flex items-center gap-0.5 min-w-0 truncate">
                      <Calendar className="h-2.5 w-2.5 md:h-3 md:w-3 shrink-0 opacity-70" />
                      <span className="truncate">{listing.vehicleDetails.year}</span>
                    </span>
                  ) : (
                    <span />
                  )}
                  {listing.vehicleDetails.mileage != null ? (
                    <span className="inline-flex items-center gap-0.5 min-w-0 truncate justify-self-center">
                      <Gauge className="h-2.5 w-2.5 md:h-3 md:w-3 shrink-0 opacity-70" />
                      <span className="truncate">
                        {formatMileage(listing.vehicleDetails.mileage)} mil
                      </span>
                    </span>
                  ) : (
                    <span />
                  )}
                  {listing.vehicleDetails.transmission ? (
                    <span className="inline-flex items-center gap-0.5 min-w-0 truncate justify-self-end">
                      <Settings className="h-2.5 w-2.5 md:h-3 md:w-3 shrink-0 opacity-70" />
                      <span className="truncate">
                        {(() => {
                          const t = listing.vehicleDetails.transmission;
                          const key = t.toUpperCase() as TransmissionType;
                          return transmissionTypeLabels(language)[key] || t;
                        })()}
                      </span>
                    </span>
                  ) : (
                    <span />
                  )}
                </div>
                <div className="flex items-center justify-center gap-4 min-w-0">
                  {listing.vehicleDetails.bodyType ? (
                    <span className="inline-flex items-center gap-0.5 truncate max-w-[48%]">
                      <Car className="h-2.5 w-2.5 md:h-3 md:w-3 shrink-0 opacity-70" />
                      {getBodyTypeLabel(
                        listing.vehicleDetails.bodyType,
                        language,
                      )}
                    </span>
                  ) : null}
                  {listing.vehicleDetails.fuelType ? (
                    <span className="inline-flex items-center gap-0.5 truncate max-w-[48%]">
                      <Fuel className="h-2.5 w-2.5 md:h-3 md:w-3 shrink-0 opacity-70" />
                      {fuelTypeLabels(language)[
                        listing.vehicleDetails.fuelType.toUpperCase() as VehicleFuelType
                      ] || listing.vehicleDetails.fuelType}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="mt-auto pt-1 flex items-end justify-between gap-1.5 min-w-0">
                <div className="min-w-0 shrink">{renderGridPlate()}</div>
                <div className="shrink-0 text-right">
                  {listing.listingType === "direct-purchase" && (
                    <p className={`${directPriceClass} text-sm leading-tight`}>
                      {listing.directPurchaseDetails?.currentPrice.toLocaleString(
                        "sv-SE",
                      )}{" "}
                      :-
                    </p>
                  )}
                  {listing.listingType === "auction" &&
                    listing.auctionDetails && (
                      <p className={`${auctionPriceClass} text-sm leading-tight`}>
                        {listing.auctionDetails.currentBid != null &&
                        listing.auctionDetails.currentBid > 0
                          ? `${listing.auctionDetails.currentBid.toLocaleString("sv-SE")} :-`
                          : `${(listing.auctionDetails.startingPrice ?? 0).toLocaleString("sv-SE")} :-`}
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>
        </Card>
        {showDescriptionPopup && (
          <DescriptionPopup
            description={
              listing.description ||
              (vcLabels.noDescriptionAvailable ?? "No description available")
            }
            title={listing.title}
            onClose={() => setShowDescriptionPopup(false)}
          />
        )}
      </>
    );
  }

  if (effectiveViewMode === "narrow") {
    return (
      <>
        <Card
          className={`overflow-hidden ${cardShadowClass} cursor-pointer group animate-fade-in p-0 h-[180px] sm:h-[120px] md:h-[180px] min-h-[180px] sm:min-h-[120px] md:min-h-[180px]`}
          style={cardTransitionStyle}
          onClick={handleCardClick}
          onMouseEnter={() => setCardHovered(true)}
          onMouseLeave={() => setCardHovered(false)}
        >
          <div className="flex h-full min-w-0">
            {/* Bild: mobil 40%, tablet 35%, desktop narrow ~20% */}
            <div
              className="relative flex-[0_0_40%] sm:flex-[0_0_35%] md:flex-[0_0_20%] h-full overflow-hidden rounded-l-lg bg-gray-200 dark:bg-gray-800 min-w-0"
            >
              <ListingCardThumbnail
                src={listing.thumbnail}
                hoverSrc={listing.hoverThumbnail}
                alt={listing.title}
                className="absolute inset-0 w-full h-full"
                imgClassName="absolute inset-0 w-full h-full object-cover object-center"
                preloadHover={cardHovered}
              />
              {listing.listingType && (
                <div className="absolute top-1 left-1 z-[1]">
                  <SaleTypeBadge
                    saleType={
                      listing.listingType === "auction"
                        ? "auction"
                        : "direct-purchase"
                    }
                    className="text-[8px] sm:text-[10px] shadow-sm"
                  />
                </div>
              )}
            </div>

            <div className="relative flex-1 min-w-0 p-2 sm:p-2.5 md:p-3 flex flex-col overflow-hidden">
              {/* Info + hjärta: absolute så de inte skapar space under titel (desktop narrow) */}
              <div className="absolute top-2 right-2 sm:top-2.5 sm:right-2.5 md:top-3 md:right-3 z-10 flex items-center gap-1 sm:gap-1.5">
                <button
                  type="button"
                  className="hidden md:inline-flex hover:scale-[1.15] p-0.5 rounded-full transition items-center justify-center w-8 h-8 md:w-10 md:h-10 bg-background hover:bg-accent/10"
                  onClick={handleDescriptionClick}
                  aria-label={carCardLabels.showDescription}
                >
                  <Info className="w-5 h-5 md:w-7 md:h-7 text-muted-foreground" />
                </button>
                <button
                  className="hover:scale-[1.15] p-0.5 rounded-full transition w-8 h-8 md:w-10 md:h-10 flex items-center justify-center"
                  onClick={handleFavoriteToggle}
                  aria-label={
                    isFavorite ? "Ta bort favorit" : "Lägg till favorit"
                  }
                >
                  <Heart
                    className={`w-6 h-6 sm:w-9 sm:h-9 md:w-8 md:h-8 ${isFavorite ? "text-red-500 fill-red-500" : "text-gray-400"} transition`}
                    fill={isFavorite ? "currentColor" : "none"}
                  />
                </button>
              </div>
              {/* Titel + märke/modell (regnr flyttas ner under bilinfo) */}
              <div className="flex-shrink-0 pr-10 sm:pr-12 md:pr-14 min-w-0">
                <h3 className="text-base sm:text-sm md:text-base font-semibold group-hover:text-accent transition-colors truncate">
                  {listing.title}
                </h3>
                <div className="flex items-center gap-2 flex-wrap mt-0.5 mb-1.5 pb-[2px] min-w-0">
                  <span className="text-[11px] sm:text-sm text-primary/70 truncate min-w-0">
                    {listing.vehicleDetails.make} {listing.vehicleDetails.model}
                  </span>
                </div>
              </div>

              {/* Fordonsinfo: större font på mobil */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm sm:text-xs md:flex md:flex-wrap md:items-center md:gap-x-2 md:gap-y-1 flex-shrink-0 min-w-0 max-w-full">
                <div className="inline-flex items-center gap-1 text-foreground shrink-0">
                  <Gauge className="w-3 h-3 flex-shrink-0" />
                  <span className="whitespace-nowrap">
                    {formatMileage(listing.vehicleDetails.mileage)} mil
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 text-foreground shrink-0">
                  <Calendar className="w-3 h-3 flex-shrink-0" />
                  <span className="whitespace-nowrap">
                    {listing.vehicleDetails.year}
                  </span>
                </div>
                {listing.vehicleDetails.transmission ? (
                  <div className="inline-flex items-center gap-1 text-foreground shrink-0">
                    <Settings className="w-3 h-3 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {(() => {
                        const t = listing.vehicleDetails.transmission;
                        if (!t) return "";
                        const key = t.toUpperCase() as TransmissionType;
                        return transmissionTypeLabels(language)[key] || t;
                      })()}
                    </span>
                  </div>
                ) : null}
                {listing.vehicleDetails.fuelType ? (
                  <div className="inline-flex items-center gap-1 text-foreground shrink-0">
                    <Fuel className="w-3 h-3 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {(() => {
                        const f = listing.vehicleDetails.fuelType;
                        if (!f) return "";
                        const key = f.toUpperCase() as VehicleFuelType;
                        return fuelTypeLabels(language)[key] || f;
                      })()}
                    </span>
                  </div>
                ) : null}
                <div className="inline-flex items-center gap-1 text-foreground shrink-0">
                  <Droplet
                    className="w-3 h-3 flex-shrink-0"
                    style={{ color: getColorHex(listing.vehicleDetails.color) }}
                  />
                  <span className="whitespace-nowrap">
                    {getColorLabel(listing.vehicleDetails.color, language)}
                  </span>
                </div>
                {listing.vehicleDetails.bodyType ? (
                  <div className="inline-flex items-center gap-1 text-foreground shrink-0">
                    <Hash className="w-3 h-3 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {getBodyTypeLabel(
                        listing.vehicleDetails.bodyType,
                        language,
                      )}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Reg nr under bilinfo */}
              {!hideLicensePlate && listing.vehicleDetails.regNumber && (
                <div className="flex-shrink-0 mt-0.5 min-w-0">
                  <LicensePlate
                    lpn={listing.vehicleDetails.regNumber}
                    className="!text-xs sm:!text-xs md:!text-xs"
                  />
                </div>
              )}

              {/* Bottenrad: tid/pris */}
              <div className="mt-auto pt-1 flex-shrink-0 min-w-0 w-full">
                {/* Mobile narrow: tid ovanför pris */}
                <div className="md:hidden flex flex-col items-end min-w-0 flex-shrink-0 text-right gap-1 w-full">
                  {listing.listingType === "auction" && auctionEndTime && (
                    <CompactAuctionTimer
                      endTime={auctionEndTime}
                      className="text-sm font-medium"
                    />
                  )}

                  {listing.listingType === "direct-purchase" &&
                    listing.directPurchaseDetails?.currentPrice != null && (
                      <div className="leading-tight w-full text-right">
                        {listing.directPurchaseDetails?.originalPrice &&
                        listing.directPurchaseDetails.originalPrice >
                          listing.directPurchaseDetails.currentPrice ? (
                          <>
                            <p className="text-xs text-gray-500 line-through leading-none truncate">
                              {listing.directPurchaseDetails.originalPrice.toLocaleString()}{" "}
                              kr
                            </p>
                            <p
                              className={`${directPriceClass} leading-tight truncate text-base sm:text-base`}
                            >
                              {listing.directPurchaseDetails?.currentPrice.toLocaleString()}{" "}
                              kr
                            </p>
                          </>
                        ) : (
                          <p
                            className={`${directPriceClass} leading-tight truncate text-base sm:text-base`}
                          >
                            {listing.directPurchaseDetails?.currentPrice.toLocaleString()}{" "}
                            kr
                          </p>
                        )}
                      </div>
                    )}

                  {listing.listingType === "auction" &&
                    listing.auctionDetails && (
                      <p
                        className={`${auctionPriceClass} leading-tight truncate text-base sm:text-base`}
                      >
                        {listing.auctionDetails.currentBid != null &&
                        listing.auctionDetails.currentBid > 0
                          ? `${listing.auctionDetails.currentBid.toLocaleString()} kr`
                          : `${(listing.auctionDetails.startingPrice ?? 0).toLocaleString()} kr`}
                      </p>
                    )}
                </div>

                {/* Desktop narrow: tid vänster, pris höger */}
                <div className="hidden md:flex items-center justify-between gap-x-2 w-full">
                  <div className="flex-1 min-w-0">
                    {listing.listingType === "auction" && auctionEndTime && (
                      <CompactAuctionTimer
                        endTime={auctionEndTime}
                        className="text-sm md:text-base font-medium"
                      />
                    )}
                  </div>

                  <div className="flex flex-col items-end min-w-0 flex-shrink-0 text-right gap-2">
                    {listing.listingType === "direct-purchase" &&
                      listing.directPurchaseDetails?.currentPrice != null && (
                        <div className="leading-tight w-full text-right">
                          {listing.directPurchaseDetails?.originalPrice &&
                          listing.directPurchaseDetails.originalPrice >
                            listing.directPurchaseDetails.currentPrice ? (
                            <>
                              <p className="text-xs text-gray-500 line-through leading-none truncate">
                                {listing.directPurchaseDetails.originalPrice.toLocaleString()}{" "}
                                kr
                              </p>
                              <p
                                className={`${directPriceClass} leading-tight truncate text-base sm:text-base md:text-lg`}
                              >
                                {listing.directPurchaseDetails?.currentPrice.toLocaleString()}{" "}
                                kr
                              </p>
                            </>
                          ) : (
                            <p
                              className={`${directPriceClass} leading-tight truncate text-base sm:text-base md:text-lg`}
                            >
                              {listing.directPurchaseDetails?.currentPrice.toLocaleString()}{" "}
                              kr
                            </p>
                          )}
                        </div>
                      )}

                    {listing.listingType === "auction" &&
                      listing.auctionDetails && (
                        <p
                          className={`${auctionPriceClass} leading-tight truncate text-base sm:text-base md:text-lg`}
                        >
                          {listing.auctionDetails.currentBid != null &&
                          listing.auctionDetails.currentBid > 0
                            ? `${listing.auctionDetails.currentBid.toLocaleString()} kr`
                            : `${(listing.auctionDetails.startingPrice ?? 0).toLocaleString()} kr`}
                        </p>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {showDescriptionPopup && (
          <DescriptionPopup
            description={
              listing.description ||
              (vcLabels.noDescriptionAvailable ?? "No description available")
            }
            title={listing.title}
            onClose={() => setShowDescriptionPopup(false)}
          />
        )}
      </>
    );
  }

  // Wide view (original layout)
  return (
    <>
      <Card
        className={`overflow-hidden ${cardShadowClass} cursor-pointer group animate-fade-in`}
        style={cardTransitionStyle}
        onClick={handleCardClick}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
      >
        <div className="flex flex-col md:flex-row h-full">
          {/* Bilden */}
          <div
            className="relative flex-shrink-0 h-48 md:h-auto w-full md:w-2/5 overflow-hidden"
          >
            <ListingCardThumbnail
              src={listing.thumbnail}
              hoverSrc={listing.hoverThumbnail}
              alt={listing.title}
              className="absolute inset-0 w-full h-full"
              imgClassName="absolute inset-0 w-full h-full object-cover"
              imgStyle={{
                objectPosition: "center",
                imageRendering: "auto",
              }}
              loading="lazy"
              preloadHover={cardHovered}
            />

            {/* Overlay buttons on mobile - top right */}
            <div className="absolute top-1 right-1 flex items-center gap-2 md:hidden z-10">
              {listing.listingType && (
                <div className="flex items-center">
                  <SaleTypeBadge
                    saleType={
                      listing.listingType === "direct-purchase"
                        ? "direct-purchase"
                        : "auction"
                    }
                    className="text-xs"
                  />
                </div>
              )}
              <button
                className="p-1.5 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDescriptionClick(e);
                }}
                aria-label={carCardLabels.showDescription}
              >
                <Info className="w-7 h-7 text-white" />
              </button>
              <button
                className="p-1.5 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFavoriteToggle(e);
                }}
                aria-label={
                  isFavorite
                    ? carCardLabels.removeFavorite
                    : carCardLabels.addFavorite
                }
              >
                <Heart
                  className={`w-7 h-7 ${
                    isFavorite ? "fill-red-500 text-red-500" : "text-white"
                  } transition`}
                  fill={isFavorite ? "currentColor" : "none"}
                />
              </button>
            </div>
          </div>

          {/* Detaljer */}
          <div
            className={`relative flex-1 p-3 md:p-4 ${
              isDark ? "bg-black/40" : "bg-white/90"
            } backdrop-blur-sm flex flex-col justify-between`}
          >
            {/* Mobile layout – större font i wide view på mobil */}
            <div className="md:hidden space-y-2">
              {/* Annonstitel */}
              <h3
                className={`text-base font-semibold ${
                  isDark ? "text-white" : "text-gray-800"
                } group-hover:text-accent transition-colors leading-tight`}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {listing.title}
              </h3>

              <span className="text-sm text-primary/70 block">
                {listing.vehicleDetails.make} {listing.vehicleDetails.model}
              </span>

              {/* Mileage Year Color */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <div className="flex items-center gap-1">
                    <Gauge className="w-3 h-3 flex-shrink-0" />
                    <span>
                      {formatMileage(listing.vehicleDetails.mileage)} mil
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{listing.vehicleDetails.year}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Droplet
                      className="w-3 h-3 flex-shrink-0"
                      style={{
                        color: getColorHex(listing.vehicleDetails.color),
                      }}
                    />
                    <span className="truncate">
                      {listing.vehicleDetails.color}
                    </span>
                  </div>
                </div>
              </div>

              {/* växlingstyp bränsle kaross          Sänkt med... */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  {listing.vehicleDetails.transmission && (
                    <div className="flex items-center gap-1">
                      <Settings className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {(() => {
                          const transmission =
                            listing.vehicleDetails.transmission;
                          if (!transmission) return "";
                          const lang = language;
                          const transmissionLabels =
                            transmissionTypeLabels(lang);
                          const key =
                            transmission.toUpperCase() as TransmissionType;
                          return transmissionLabels[key] || transmission;
                        })()}
                      </span>
                    </div>
                  )}
                  {listing.vehicleDetails.fuelType && (
                    <div className="flex items-center gap-1">
                      <Fuel className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {(() => {
                          const fuelType = listing.vehicleDetails.fuelType;
                          if (!fuelType) return "";
                          const lang = language;
                          const fuelLabels = fuelTypeLabels(lang);
                          const key = fuelType.toUpperCase() as VehicleFuelType;
                          return fuelLabels[key] || fuelType;
                        })()}
                      </span>
                    </div>
                  )}
                  {listing.vehicleDetails.bodyType && (
                    <div className="flex items-center gap-1">
                      <Hash className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {getBodyTypeLabel(
                          listing.vehicleDetails.bodyType,
                          language,
                        )}
                      </span>
                    </div>
                  )}
                </div>
                {listing.listingType === "direct-purchase" &&
                  listing.directPurchaseDetails.originalPrice &&
                  listing.directPurchaseDetails.originalPrice >
                    listing.directPurchaseDetails.currentPrice && (
                    <div className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 font-medium">
                      <TrendingDown className="w-3 h-3" />
                      <span>
                        {(
                          listing.directPurchaseDetails.originalPrice -
                          listing.directPurchaseDetails.currentPrice
                        ).toLocaleString()}{" "}
                        kr
                      </span>
                    </div>
                  )}
              </div>

              {/* Reg nr under bilinfo (wide mobil) */}
              {!hideLicensePlate && listing.vehicleDetails.regNumber && (
                <div>
                  <LicensePlate lpn={listing.vehicleDetails.regNumber} />
                </div>
              )}

              {/* Bottom row: time left, price right (mobile wide) */}
              {listing.listingType === "auction" && auctionEndTime && (
                <div className="flex items-center justify-between mt-1 gap-3">
                  <CompactAuctionTimer
                    endTime={auctionEndTime}
                    className="text-sm sm:text-base font-medium"
                  />
                  <p className={`text-lg ${auctionPriceClass}`}>
                    {listing.auctionDetails?.currentBid &&
                    listing.auctionDetails?.currentBid > 0
                      ? listing.auctionDetails.currentBid.toLocaleString()
                      : listing.auctionDetails?.startingPrice?.toLocaleString() ||
                        0}{" "}
                    kr
                  </p>
                </div>
              )}

              {listing.listingType === "direct-purchase" &&
                listing.directPurchaseDetails?.currentPrice != null && (
                  <div className="flex items-center justify-end mt-1">
                    <p className={`text-lg ${directPriceClass}`}>
                      {listing.directPurchaseDetails.currentPrice.toLocaleString()}{" "}
                      kr
                    </p>
                  </div>
                )}
            </div>

            {/* Desktop layout */}
            <div className="hidden md:block space-y-2 md:space-y-3">
              <div className="flex items-start gap-2 mb-1 relative">
                <div className="flex-1 min-w-0">
                  <h3
                    className={`text-lg font-semibold ${
                      isDark ? "text-white" : "text-gray-800"
                    } group-hover:text-accent transition-colors leading-tight`}
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {listing.title}
                  </h3>
                  <p
                    className={`text-sm ${
                      isDark ? "text-white/70" : "text-gray-600"
                    } truncate mt-0.5`}
                  >
                    {listing.vehicleDetails.make} {listing.vehicleDetails.model}
                  </p>
                </div>
                <button
                  type="button"
                  className="hover:scale-[1.15] p-1 rounded-full transition z-10 flex-shrink-0 w-8 h-8 md:w-11 md:h-11 flex items-center justify-center bg-background hover:bg-accent/10"
                  onClick={handleDescriptionClick}
                  aria-label={carCardLabels.showDescription}
                >
                  <Info
                    className={`w-6 h-6 md:w-8 md:h-8 text-muted-foreground`}
                  />
                </button>
                <button
                  className="hover:scale-[1.15] p-1 rounded-full transition z-10 flex-shrink-0 w-8 h-8 md:w-11 md:h-11 flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFavoriteToggle(e);
                  }}
                  aria-label={
                    isFavorite
                      ? carCardLabels.removeFavorite
                      : carCardLabels.addFavorite
                  }
                >
                  <Heart
                    className={`w-6 h-6 md:w-8 md:h-8 ${
                      isFavorite ? "text-red-500 fill-red-500" : "text-gray-400"
                    } transition`}
                    fill={isFavorite ? "currentColor" : "none"}
                  />
                </button>
              </div>
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-1.5 md:gap-2">
                <div>
                  {listing.listingType && (
                    <div className="flex items-center gap-1.5 md:gap-2 mb-2">
                      <SaleTypeBadge
                        saleType={
                          listing.listingType === "direct-purchase"
                            ? "direct-purchase"
                            : "auction"
                        }
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 md:gap-x-2 md:gap-y-1">
                    <div
                      className={`inline-flex items-center gap-1 shrink-0 ${
                        isDark ? "text-white/90" : "text-foreground"
                      }`}
                    >
                      <Gauge className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                      <span className="text-xs md:text-sm whitespace-nowrap">
                        {formatMileage(listing.vehicleDetails.mileage)} mil
                      </span>
                    </div>
                    <div
                      className={`inline-flex items-center gap-1 shrink-0 ${
                        isDark ? "text-white/90" : "text-foreground"
                      }`}
                    >
                      <Calendar className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                      <span className="text-xs md:text-sm whitespace-nowrap">
                        {listing.vehicleDetails.year}
                      </span>
                    </div>
                    <div
                      className={`inline-flex items-center gap-1 shrink-0 ${
                        isDark ? "text-white/90" : "text-foreground"
                      }`}
                    >
                      <Droplet
                        className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0"
                        style={{
                          color: getColorHex(listing.vehicleDetails.color),
                        }}
                      />
                      <span className="text-xs md:text-sm whitespace-nowrap">
                        {getColorLabel(listing.vehicleDetails.color, language)}
                      </span>
                    </div>
                    {listing.vehicleDetails.transmission && (
                      <div
                        className={`inline-flex items-center gap-1 shrink-0 ${
                          isDark ? "text-white/90" : "text-foreground"
                        }`}
                      >
                        <Settings className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="text-xs md:text-sm whitespace-nowrap">
                          {(() => {
                            const transmission =
                              listing.vehicleDetails.transmission;
                            if (!transmission) return "";
                            const lang = language;
                            const transmissionLabels =
                              transmissionTypeLabels(lang);
                            const key =
                              transmission.toUpperCase() as TransmissionType;
                            return transmissionLabels[key] || transmission;
                          })()}
                        </span>
                      </div>
                    )}
                    {listing.vehicleDetails.fuelType && (
                      <div
                        className={`inline-flex items-center gap-1 shrink-0 ${
                          isDark ? "text-white/90" : "text-foreground"
                        }`}
                      >
                        <Fuel className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="text-xs md:text-sm whitespace-nowrap">
                          {(() => {
                            const fuelType = listing.vehicleDetails.fuelType;
                            if (!fuelType) return "";
                            const lang = language;
                            const fuelLabels = fuelTypeLabels(lang);
                            const key =
                              fuelType.toUpperCase() as VehicleFuelType;
                            return fuelLabels[key] || fuelType;
                          })()}
                        </span>
                      </div>
                    )}
                    {listing.vehicleDetails.bodyType && (
                      <div
                        className={`flex items-center gap-1 md:gap-1.5 ${
                          isDark ? "text-white/90" : "text-foreground"
                        }`}
                      >
                        <Hash className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="text-xs md:text-sm truncate">
                          {getBodyTypeLabel(
                            listing.vehicleDetails.bodyType,
                            language,
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Reg nr under bilinfo (desktop wide) */}
                  {!hideLicensePlate && listing.vehicleDetails.regNumber && (
                    <div className="mt-2">
                      <LicensePlate lpn={listing.vehicleDetails.regNumber} />
                    </div>
                  )}
                </div>
              </div>
              {/* Bottom row (desktop wide): time left, price right */}
              <div className="flex flex-row w-full justify-between items-center mt-1 md:mt-0">
                {/* Left: time (auction only) */}
                <div className="flex-shrink-0">
                  {listing.listingType === "auction" && auctionEndTime && (
                    <CompactAuctionTimer
                      endTime={auctionEndTime}
                      className="text-sm md:text-base font-medium"
                    />
                  )}
                </div>

                {/* Right: price */}
                <div className="flex flex-col items-end gap-2 text-right">
                  {listing.listingType === "direct-purchase" ? (
                    <div>
                      <p className={`text-2xl md:text-3xl ${directPriceClass}`}>
                        {listing.directPurchaseDetails.currentPrice.toLocaleString()}{" "}
                        kr
                      </p>
                      {listing.directPurchaseDetails.originalPrice &&
                        listing.directPurchaseDetails.originalPrice >
                          listing.directPurchaseDetails.currentPrice && (
                          <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                            {vcLabels.reducedBy ?? "Reduced by"}{" "}
                            {(
                              listing.directPurchaseDetails.originalPrice -
                              listing.directPurchaseDetails.currentPrice
                            ).toLocaleString()}{" "}
                            kr
                          </p>
                        )}
                      <p className={`text-sm text-primary/60 mt-0.5`}>
                        {vcLabels.price ?? "Price"}
                      </p>
                    </div>
                  ) : (
                    <>
                      {listing.auctionDetails?.currentBid == undefined ||
                      listing.auctionDetails?.currentBid == 0 ? (
                        <>
                          <p
                            className={`text-2xl md:text-3xl ${auctionPriceClass}`}
                          >
                            {listing.auctionDetails?.startingPrice?.toLocaleString() ||
                              0}{" "}
                            kr
                          </p>
                          <p className={`text-sm text-primary/60 mt-0.5`}>
                            {vcLabels.startingPrice ?? "Starting price"}
                          </p>
                        </>
                      ) : (
                        <>
                          <p
                            className={`text-2xl md:text-3xl ${auctionPriceClass}`}
                          >
                            {listing.auctionDetails?.currentBid?.toLocaleString() ||
                              0}{" "}
                            kr
                          </p>
                          <p className={`text-sm text-primary/60 mt-0.5`}>
                            {vcLabels.currentBid ?? "Current bid"}
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {showDescriptionPopup && (
        <DescriptionPopup
          description={
            listing.description ||
            (vcLabels.noDescriptionAvailable ?? "No description available")
          }
          title={listing.title}
          onClose={() => setShowDescriptionPopup(false)}
        />
      )}
    </>
  );
};

export default React.memo(CarCard);
